import React, {useEffect, useState, useRef} from "react";
import useAxios from "../../utils/useAxios";
import {useTranslation} from "react-i18next";
import {Container} from "react-bootstrap";
import {useHistory, useParams, useLocation} from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import {COUNTRY_OPTIONS, LEVEL_OPTIONS, Truncated} from "../../constants";
import SelectInput from "../../components/SelectInput";
import {getOptionName} from "../../utils/getOptionName";
import PageLoader from "../../components/PageLoader";
import { toast } from "react-toastify";
import RatingExportModal from "./RatingExportModal";
import TournamentShareModal from "../tournaments/export/TournamentShareModal";
import { useTheme } from "../../contexts/ThemeContext";

const RatingTable = ({ ratingId: ratingIdProp, highlightPlayerId, isOpen = true, onClose } = {}) => {
    const { id: paramId } = useParams();
    const id = ratingIdProp || paramId;
    // Modal chrome (compact header, disabled date-range editing, its own X)
    // only when there's actually a way to close this instance - true for
    // Header.js's always-mounted rating-badge modal (which always passes
    // onClose), false both for the standalone /admin/ratings/:id route AND
    // for a tournament's own "Rating" tab (Tournament.js passes ratingId
    // explicitly there too, same as the modal, but nothing to close it
    // into - it's a real page in both cases). Used to key off ratingIdProp
    // alone, which conflated "was an id passed in" with "is this a modal" -
    // wrong for that third, non-modal-but-explicit-id case.
    const isModal = !!onClose;
    // The tournament-tab case specifically (see the comment above) - an
    // explicit ratingId but no onClose. Needs its own (lighter) chrome:
    // Tournament.js already wraps every tab's content in its own
    // <Container>, so this rendering a SECOND <Container className=
    // "page_container"> around itself doubled up the side padding/max-width
    // squeeze; and the standalone route's own back-arrow+title nav row is
    // redundant here too - TournamentHeader's own tab bar already shows the
    // tournament name and which tab is active, so that row just added dead
    // top space repeating a title already on screen one row up.
    const isEmbedded = !isModal && !!ratingIdProp;

    const history = useHistory();
    const location = useLocation();
    const {t} = useTranslation();
    const api = useAxios();
    const { compactTable } = useTheme();

    const searchParams = new URLSearchParams(location.search);
    const initialLevel = searchParams.get("level") || "";

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [levelFilter, setLevelFilter] = useState(initialLevel);
    const [exportModalRatingId, setExportModalRatingId] = useState(null);
    const [isShareOpen, setIsShareOpen] = useState(false);

    // A rating has no page of its own to share - point at its most recent
    // tournament's own rating tab instead.
    const lastTournament = data?.tournaments?.length > 0 ? data.tournaments[data.tournaments.length - 1] : null;
    const shareUrl = lastTournament ? `${window.location.origin}/tournaments/${lastTournament.id}?tab=rating&show_rating=true` : null;

    const handleShare = (e) => {
        e.preventDefault();
        if (shareUrl) {
            setIsShareOpen(true);
        } else {
            toast.warning(t("This rating has no tournaments yet."));
        }
    };

    const fetchTable = (level = "") => {
        setLoading(true);

        const params = {};
        if (level) params.level = level;

        api.get(`/api/v1.0/ratings/${id}/table/`, {params})
            .then(res => setData(res.data))
            .catch(console.error)
            .finally(() => setLoading(false));
    };
    // isOpen matters for the modal usage (Header.js's own always-mounted
    // instance, only shown/hidden via CustomModal) - without it in the deps,
    // the table only ever fetched once on first open and kept showing that
    // same stale snapshot on every reopen, e.g. after a rating settings
    // save/reset recalculated scores. The standalone route usage (isModal
    // false) leaves isOpen at its default `true`, so this is unaffected there.
    useEffect(() => {
        if (id && isOpen) fetchTable(initialLevel);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isOpen]);

    const updateUrl = (level) => {
        const params = new URLSearchParams(location.search);

        if (level) params.set("level", level);
        else params.delete("level");

        history.replace({
            pathname: location.pathname,
            search: params.toString()
        });
    };

    const handleLevelSelect = (value) => {
        setLevelFilter(value);
        updateUrl(value);
        fetchTable(value);
    };

    const tableWrapperRef = useRef(null);
    const highlightedRowRef = useRef(null);

    useEffect(() => {
        if (tableWrapperRef.current && data?.tournaments?.length > 0) {
            setTimeout(() => {
                if (tableWrapperRef.current) {
                    tableWrapperRef.current.scrollLeft = tableWrapperRef.current.scrollWidth;
                }
            }, 50);
        }
    }, [data?.tournaments]);

    // Jumps to and flashes the row for whoever was clicked from elsewhere
    // (a rating badge on the Участники/Рейтинг tabs) - the table itself has
    // no other way to locate one player among what can be a long list.
    // Runs after the horizontal-scroll-to-latest-tournament effect above so
    // it isn't fighting that effect's own setTimeout for tableWrapperRef.
    useEffect(() => {
        if (highlightPlayerId && highlightedRowRef.current) {
            setTimeout(() => {
                highlightedRowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 100);
        }
    }, [highlightPlayerId, data?.players]);

    if (loading) return <PageLoader />;
    if (!data) return <div style={{ padding: "20px", textAlign: "center" }}>{t("Failed to load rating data or rating not found.")}</div>;

    const tournaments = data?.tournaments || [];
    const players = data?.players || [];
    const name = data?.name;
    const updateRatingDate = async (field, value) => {
        try {
            setData(prev => ({ ...prev, [field]: value }));
            await api.patch(`/api/v1.0/ratings/${id}/`, { [field]: value });
            fetchTable(levelFilter);
        } catch (err) {
            console.error(err);
        }
    };

    const Wrapper = isModal ? "form" : (isEmbedded ? "div" : Container);
    // id targeted by TournamentExportModal.js/ScreenshotButton.js when this
    // renders as a tournament's own "Rating" tab (see TournamentHeader.js's
    // SCREENSHOT_TARGETS).
    const wrapperProps = isModal
        ? { className: "modal-form" }
        : isEmbedded
            ? { id: "rating-block" }
            : { className: "page_container" };

    return (
        <Wrapper {...wrapperProps}>
            {isModal ? (
                <div className="modal-sticky-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
                    <h2 style={{ width: "auto", margin: 0, flex: 1, minWidth: 0 }}>
                        <FeatherIcon icon="bar-chart-2" />{name}
                    </h2>
                    {/* Own close icon here instead of CustomModal's default sticky X
                        (hideCloseButton on the caller's <CustomModal>) - that X sits
                        in the same top-right corner this row's own icon(s) reach via
                        justify-content:space-between, so the two overlapped. Same
                        fix/pattern as ClubProfileModal/PlayerProfileModal's own
                        profile-header-icon-btn row. */}
                    <div style={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}>
                        <FeatherIcon icon="share-2" size={16} className="profile-header-icon-btn" onClick={handleShare} />
                        <FeatherIcon icon="download" size={16} className="profile-header-icon-btn" onClick={() => setExportModalRatingId(id)} />
                        <FeatherIcon icon="x" size={16} className="profile-header-icon-btn" onClick={onClose} />
                    </div>
                </div>
            ) : isEmbedded ? (
                // No back-arrow here - TournamentHeader's own tab bar already
                // covers "leave this tab"/"which tab is active". The rating's
                // own name still shows though - it's the actual rating BASE's
                // name (e.g. a club-wide rating this tournament feeds into),
                // not the tournament's own name TournamentHeader already
                // shows, so it's real information, not a repeated title.
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", marginTop: "10px", marginBottom: "8px" }}>
                    <div style={{ fontSize: "13px", color: "var(--secondary-color)", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {name}
                    </div>
                    <div style={{ display: "flex", gap: "16px", flexShrink: 0 }}>
                        <a href="" onClick={handleShare} title={t("Share")}>
                            <FeatherIcon icon="share-2" size={16} />
                        </a>
                        <a href="" onClick={(e) => { e.preventDefault(); setExportModalRatingId(id); }} title={t("Export")}>
                            <FeatherIcon icon="download" size={16} />
                        </a>
                    </div>
                </div>
            ) : (
                <nav className="tabs-container">
                    <ul className="tabs">
                        <li>
                            <a href="#" onClick={e => {
                                e.preventDefault();
                                if (window.history.length > 1 && (history.action === "PUSH" || history.action === "REPLACE" || (document.referrer && document.referrer.includes(window.location.host)))) {
                                    history.goBack();
                                } else {
                                    const parentRoute = location.pathname.split('/').slice(0, -1).join('/');
                                    history.push(parentRoute || "/ratings");
                                }
                            }}>
                                <FeatherIcon icon="arrow-left" size={16}/>
                            </a>
                        </li>
                        <li className="easy-black">{name}</li>
                        <li className="right">
                            <ul className="tabs">
                                <li>
                                    <a href="" onClick={handleShare}>
                                        <FeatherIcon icon="share-2"/>
                                    </a>
                                </li>
                                <li>
                                    <a href="" onClick={(e) => { e.preventDefault(); setExportModalRatingId(id); }}>
                                        <FeatherIcon icon="download"/>
                                    </a>
                                </li>
                            </ul>
                        </li>
                    </ul>
                </nav>
            )}

            <div className="filters" style={{marginTop: "-15px", marginBottom: "-5px"}}>
                <div>
                    <label>{t("Period")} {t("from")}</label>
                    <input
                        type="date"
                        value={data.start_date?.slice(0, 10) || ""}
                        onChange={e => !isModal && updateRatingDate("start_date", e.target.value)}
                        onClick={e => !isModal && e.target.showPicker && e.target.showPicker()}
                        style={{background: "var(--white"}}
                        disabled={isModal}
                    />
                </div>

                <div>
                    <label>{t("Period")} {t("to")}</label>
                    <input
                        type="date"
                        value={data.end_date?.slice(0, 10) || ""}
                        onChange={e => !isModal && updateRatingDate("end_date", e.target.value)}
                        onClick={e => !isModal && e.target.showPicker && e.target.showPicker()}
                        style={{background: "var(--white"}}
                        disabled={isModal}
                    />
                </div>
                <div style={{width: "100px"}}>
                    <SelectInput
                        default_text={t("All levels")}
                        data={[{id: "", name: t("All levels")}, ...LEVEL_OPTIONS]}
                        default_value={levelFilter ? parseInt(levelFilter, 10) : ""}
                        label_text={t("Level")}
                        input_name="level"
                        setInputValue={handleLevelSelect}
                        background={true}
                    />
                </div>
            </div>
            {!tournaments.length && (
                <div>{t("No tournaments in selected date range")}</div>
            )}

            {!!tournaments.length && (
                <div ref={tableWrapperRef} className={`admin-wrapper rating-table-wrapper ${!isModal ? "rating-table-wrapper-page" : "rating-table-wrapper-modal"}`} style={{ overflowX: "auto", minHeight: "min-content" }}>
                    <div className="rating-table">
                        <table width="100%" className="rating-table-classic" cellPadding="0" cellSpacing="0">
                            <thead style={{ position: "relative", overflow: "hidden" }}>
                            <tr>
                                <th className="sticky-col sticky-col-1"><center>№</center></th>
                                <th className="sticky-col sticky-col-avatar" style={{ paddingRight: 0 }}></th>
                                <th className="sticky-col sticky-col-player-info" style={{textAlign: "left", paddingLeft: "10px"}}>
                                    {t("Player")}
                                </th>
                                {/*<th className="sticky-col sticky-col-3"><center>{t("Level")}</center></th>*/}
                                {tournaments.map((t, i) => (
                                    <th key={t.id}
                                        className={`tournament-col ${tournaments.length > 2 && i === tournaments.length - 1 ? "last-tournament-mobile" : ""}`}
                                        style={
                                            tournaments.length > 2 && i === tournaments.length - 1
                                                ? { borderRight: 0 }
                                                : {}
                                        }
                                    >
                                        <center>
                                            <a
                                                href={`tournaments/${t.id}`}
                                                onClick={e => {
                                                    e.preventDefault();
                                                    history.push(`/tournaments/${t.id}`);
                                                }}
                                            >
                                                {/*{t.name}<br/>*/}
                                                {t.date_range}
                                            </a>
                                        </center>
                                    </th>
                                ))}
                                {/*<th className="spacer-col"></th>*/}
                                {/*<td className={`spacer-col ${tournaments.length > 2 ? "spacer-no-mobile" : "spacer-no-border"}`}></td>*/}
                                {tournaments.length < 6 && (
                                    <th className={`spacer-col ${tournaments.length > 2 ? "spacer-no-mobile" : "spacer-no-border"}`}></th>
                                )}
                                <th style={{ verticalAlign: "middle" }}  className="total-col"><center>{t("Total")}</center></th>
                            </tr>
                            </thead>
                            <tbody style={{ position: "relative", overflow: "hidden", padding: 0, margin: 0 }}>
                            {players.map((player, index) => {
                                const isHighlighted = highlightPlayerId != null && String(player.id) === String(highlightPlayerId);
                                return (
                                    <tr
                                        key={player.id}
                                        ref={isHighlighted ? highlightedRowRef : null}
                                        className={isHighlighted ? "rating-row-highlight" : ""}
                                    >
                                        <td className="sticky-col sticky-col-1"><center>{index + 1}</center></td>
                                        <td className="sticky-col sticky-col-avatar" style={{ paddingRight: 0 }}>
                                            <a
                                                style={{ cursor: "pointer", display: "flex", justifyContent: "center" }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    const params = new URLSearchParams(location.search);
                                                    params.set("player_id", player.id);
                                                    history.push({ search: params.toString() });
                                                }}
                                            >
                                                {player.image
                                                    ? <img src={player.image} className="thumbnail mini" alt="avatar" style={{ margin: 0 }} />
                                                    : <div className="no-thumbnail mini" style={{ margin: 0 }}><FeatherIcon icon="user"/></div>}
                                            </a>
                                        </td>
                                        <td className="sticky-col sticky-col-player-info" style={{ textAlign: "left", paddingLeft: "10px" }}>
                                            <a
                                                style={{ cursor: "pointer" }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    const params = new URLSearchParams(location.search);
                                                    params.set("player_id", player.id);
                                                    history.push({ search: params.toString() });
                                                }}
                                            >
                                                <div style={{ overflow: "hidden" }}>
                                                    <span className="player-last-name">
                                                        <span className="no-laptop-inline">{player.first_name && `${player.first_name[0]}. `}</span>
                                                        <span className="no-mobile-inline">{player.last_name}</span><span className="no-laptop-inline"><Truncated text={player.last_name} /></span></span><span className="no-mobile-inline">{player.first_name && `, ${player.first_name}`}</span><br/>
                                                    <span className="gray">
                                                    {player.city && (
                                                        <>
                                                            {player.city}
                                                            <span className="no-mobile-inline">{player.country && `, ${t(getOptionName(COUNTRY_OPTIONS, player.country))}`}</span>
                                                        </>
                                                    )}{player.city && player.level  && <>, </>} {player.level && t(getOptionName(LEVEL_OPTIONS, player.level))}
                                                    </span>
                                                </div>
                                            </a>
                                        </td>
                                        {tournaments.map((t, i) => (
                                            <td key={t.id} className={`tournament-col ${i === tournaments.length - 1 ? "last-tournament-mobile" : ""}`} style={
                                                tournaments.length > 2 && i === tournaments.length - 1
                                                    ? { borderRight: 0 }
                                                    : {}
                                            }>
                                            {/*<td key={t.id} className="tournament-col">*/}
                                                <center>{player.ratings[t.id] ?? ""}</center>
                                            </td>
                                        ))}
                                        {tournaments.length < 6 && (
                                            <td className={`spacer-col ${tournaments.length > 2 ? "spacer-no-mobile" : "spacer-no-border"}`}></td>
                                        )}
                                        <td className="total-col">
                                            <center><strong>{player.total_score}</strong></center>
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                </div>
                </div>
                )}
            <RatingExportModal
                isOpen={!!exportModalRatingId}
                onClose={() => setExportModalRatingId(null)}
                ratingId={exportModalRatingId}
            />
            <TournamentShareModal
                isOpen={isShareOpen}
                onClose={() => setIsShareOpen(false)}
                url={shareUrl}
                title={name}
                shareText={name ? t("Check out the rating {{name}}!", { name }) : undefined}
                linkLabel={t("Rating link")}
                qrCaption={t("Scan with camera to open the rating")}
            />
        </Wrapper>
    );
};

export default RatingTable;
